import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ShieldAlert, ExternalLink, ThumbsUp, ThumbsDown, Minus, X, Check, XCircle, Eye, Pencil, RefreshCw } from "lucide-react";
import {
  useAdminChallengeQuery,
  useAdminSetChallengeClipApprovalMutation,
  useAdminSetChallengeClipHashtagValidationMutation,
} from "@/generated/graphql";
import { useMutation } from "@apollo/client";
import { ADMIN_CREATE_MANUAL_CLIP_VIEW, ADMIN_FORCE_UPDATE_CHALLENGE_CLIP_VIEW } from "@/queries/queries";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

const CHALLENGE_TZ = "America/Sao_Paulo";

const AdminChallengeAudit = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { challengeId, participantId } = useParams<{ challengeId: string; participantId: string }>();
  const navigate = useNavigate();

  const [disapproveTarget, setDisapproveTarget] = useState<string | null>(null);
  const [disapproveReason, setDisapproveReason] = useState("");
  const [manualViewsTarget, setManualViewsTarget] = useState<string | null>(null);
  const [manualViewsInput, setManualViewsInput] = useState("");

  const { data, loading, error, refetch } = useAdminChallengeQuery({
    variables: { id: challengeId! },
    skip: !challengeId,
    fetchPolicy: "cache-and-network",
  });

  const [setApproval, { loading: settingApproval }] = useAdminSetChallengeClipApprovalMutation();
  const [setHashtagValidation, { loading: settingHashtag }] = useAdminSetChallengeClipHashtagValidationMutation();
  const [createManualClipView, { loading: savingManualViews }] = useMutation(ADMIN_CREATE_MANUAL_CLIP_VIEW);
  const [forceUpdateClip, { loading: forcingUpdateClip }] = useMutation(ADMIN_FORCE_UPDATE_CHALLENGE_CLIP_VIEW);

  const challenge = data?.adminChallenge;
  const participant = challenge?.participants?.find((p) => p.id === participantId) || null;
  const clips = participant?.challenge_clips || [];

  const handleApprovalClick = (clipId: string, approval: "approved" | "neutral" | "disapproved") => {
    if (approval === "disapproved") {
      setDisapproveTarget(clipId);
      setDisapproveReason("");
    } else {
      handleSetApproval(clipId, approval);
    }
  };

  const handleSetApproval = async (clipId: string, approval: "approved" | "neutral" | "disapproved", reason?: string) => {
    try {
      await setApproval({ variables: { id: clipId, approval, reason: reason || undefined } });
      toast({ title: t("admin.challenges.approvalSet") });
      setDisapproveTarget(null);
      setDisapproveReason("");
      refetch();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    }
  };

  const handleSetHashtagValidation = async (clipId: string, validated_hashtags: "validated" | "neutral" | "invalidated") => {
    try {
      await setHashtagValidation({ variables: { id: clipId, validated_hashtags } });
      toast({ title: t("admin.challenges.hashtagValidationSet") });
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.hashtagValidationError"), variant: "destructive" });
    }
  };

  const handleManualViews = async (clipId: string) => {
    const views = parseInt(manualViewsInput, 10);
    if (isNaN(views) || views < 0) {
      toast({ title: t("admin.challenges.manualViewsInvalid"), variant: "destructive" });
      return;
    }
    try {
      await createManualClipView({ variables: { clipId, views: String(views) } });
      toast({ title: t("admin.challenges.manualViewsSaved") });
      setManualViewsTarget(null);
      setManualViewsInput("");
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.manualViewsError"), variant: "destructive" });
    }
  };

  const handleForceUpdateClip = async (clipId: string) => {
    try {
      await forceUpdateClip({ variables: { clipId } });
      toast({ title: t("admin.challenges.forceUpdateClipViewSuccess") });
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.forceUpdateClipViewError"), variant: "destructive" });
    }
  };

  const backUrl = `/admin/challenges/${challengeId}/participants`;
  const colSpan = 7;

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(backUrl)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t("common.back")}
            </Button>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-primary" />
              <div>
                <h1 className="text-xl font-bold">{t("admin.challenges.auditParticipant")}</h1>
                {challenge && <p className="text-sm text-muted-foreground">{challenge.name}</p>}
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500 ml-auto">ADMIN</Badge>
          </div>

          {loading && !data ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
          ) : !participant ? (
            <div className="text-center py-8 text-muted-foreground">{t("admin.common.noResults")}</div>
          ) : (
            <>
              {/* Participant card */}
              <Card>
                <CardHeader>
                  <CardTitle>{participant.user_name || participant.user_email || "—"}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t("common.email")}</p>
                      <p className="font-medium">{participant.user_email || "—"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("admin.challenges.totalViews")}</p>
                      <p className="font-medium">{parseInt(participant.total_views || "0", 10).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("admin.challenges.clipCount")}</p>
                      <p className="font-medium">{clips.length}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("admin.challenges.createdAt")}</p>
                      <p className="font-medium">{formatDateTime(participant.createdAt, CHALLENGE_TZ)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Clips */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("admin.challenges.auditClips")} ({clips.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {clips.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">{t("admin.common.noResults")}</p>
                  ) : (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Platform</TableHead>
                            <TableHead>URL</TableHead>
                            <TableHead>{t("admin.challenges.totalViews")}</TableHead>
                            <TableHead>{t("admin.challenges.hashtagValidation")}</TableHead>
                            <TableHead>{t("admin.challenges.createdAt")}</TableHead>
                            <TableHead className="w-[160px]">{t("admin.challenges.actions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clips.map((clip) => (
                            <>
                              <TableRow key={clip.id} className={clip.admin_approval === "disapproved" ? "opacity-60" : ""}>
                                <TableCell>
                                  <span className="text-xs font-semibold uppercase px-2 py-1 rounded bg-primary/10 text-primary">
                                    {clip.platform}
                                  </span>
                                </TableCell>
                                <TableCell className="max-w-xs">
                                  <a
                                    href={clip.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-sm text-blue-400 hover:underline truncate"
                                  >
                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{clip.url}</span>
                                  </a>
                                  {clip.admin_approval === "disapproved" && clip.approval_reason && (
                                    <p className="text-xs text-red-400 mt-0.5">
                                      {t("admin.challenges.approvalReason")}: {clip.approval_reason}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell>{parseInt(clip.total_views || "0", 10).toLocaleString()}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-0.5">
                                    <Button
                                      variant={clip.validated_hashtags === "validated" ? "default" : "ghost"}
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${clip.validated_hashtags === "validated" ? "text-gray-900" : "text-emerald-500"}`}
                                      onClick={() => handleSetHashtagValidation(clip.id, "validated")}
                                      disabled={settingHashtag}
                                      title={t("admin.challenges.hashtagValidated")}
                                    >
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${clip.validated_hashtags === "neutral" ? "bg-gray-500/20 text-gray-300" : "text-muted-foreground"}`}
                                      onClick={() => handleSetHashtagValidation(clip.id, "neutral")}
                                      disabled={settingHashtag}
                                      title={t("admin.challenges.hashtagNeutral")}
                                    >
                                      <Minus className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant={clip.validated_hashtags === "invalidated" ? "destructive" : "ghost"}
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${clip.validated_hashtags === "invalidated" ? "" : "text-red-500"}`}
                                      onClick={() => handleSetHashtagValidation(clip.id, "invalidated")}
                                      disabled={settingHashtag}
                                      title={t("admin.challenges.hashtagInvalidated")}
                                    >
                                      <XCircle className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell>{formatDateTime(clip.createdAt, CHALLENGE_TZ)}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-0.5">
                                    <Button
                                      variant={clip.admin_approval === "approved" ? "default" : "ghost"}
                                      size="sm"
                                      className={clip.admin_approval === "approved" ? "text-gray-900" : "text-emerald-500"}
                                      onClick={() => handleApprovalClick(clip.id, "approved")}
                                      disabled={settingApproval}
                                      title={t("admin.challenges.approvalApprove")}
                                    >
                                      <ThumbsUp className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={clip.admin_approval === "neutral" ? "bg-gray-500/20 text-gray-300" : "text-muted-foreground"}
                                      onClick={() => handleApprovalClick(clip.id, "neutral")}
                                      disabled={settingApproval}
                                      title={t("admin.challenges.approvalNeutral")}
                                    >
                                      <Minus className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant={clip.admin_approval === "disapproved" ? "destructive" : "ghost"}
                                      size="sm"
                                      className={clip.admin_approval === "disapproved" ? "" : "text-red-500"}
                                      onClick={() => handleApprovalClick(clip.id, "disapproved")}
                                      disabled={settingApproval}
                                      title={t("admin.challenges.approvalDisapprove")}
                                    >
                                      <ThumbsDown className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-gray-300"
                                      onClick={() => { setManualViewsTarget(clip.id); setManualViewsInput(""); }}
                                      title={t("admin.challenges.manualViews")}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-blue-400"
                                      onClick={() => handleForceUpdateClip(clip.id)}
                                      disabled={forcingUpdateClip}
                                      title={t("admin.challenges.forceUpdateClipView")}
                                    >
                                      <RefreshCw className={`w-4 h-4 ${forcingUpdateClip ? "animate-spin" : ""}`} />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                              {disapproveTarget === clip.id && (
                                <TableRow key={`${clip.id}-reason`}>
                                  <TableCell colSpan={colSpan}>
                                    <div className="border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5">
                                      <p className="text-xs font-medium text-red-400">{t("admin.challenges.disapproveConfirm")}</p>
                                      <div className="space-y-1">
                                        <Label className="text-xs">{t("admin.challenges.approvalReason")}</Label>
                                        <Textarea
                                          value={disapproveReason}
                                          onChange={(e) => setDisapproveReason(e.target.value)}
                                          placeholder={t("admin.challenges.approvalReasonPlaceholder")}
                                          rows={2}
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="destructive"
                                          size="sm"
                                          onClick={() => handleSetApproval(clip.id, "disapproved", disapproveReason)}
                                          disabled={settingApproval}
                                        >
                                          <ThumbsDown className="w-3 h-3 mr-1" />
                                          {t("admin.challenges.approvalDisapprove")}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => { setDisapproveTarget(null); setDisapproveReason(""); }}
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          {t("common.cancel")}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                              {manualViewsTarget === clip.id && (
                                <TableRow key={`${clip.id}-manual-views`}>
                                  <TableCell colSpan={colSpan}>
                                    <div className="border border-blue-500/30 rounded-lg p-3 space-y-2 bg-blue-500/5">
                                      <p className="text-xs font-medium text-blue-400">{t("admin.challenges.manualViewsConfirm")}</p>
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs shrink-0">{t("admin.challenges.manualViewsLabel")}</Label>
                                        <Input
                                          type="number"
                                          min={0}
                                          value={manualViewsInput}
                                          onChange={(e) => setManualViewsInput(e.target.value)}
                                          placeholder="0"
                                          className="h-8 w-40"
                                          onKeyDown={(e) => e.key === "Enter" && handleManualViews(clip.id)}
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="default"
                                          size="sm"
                                          className="text-gray-900"
                                          onClick={() => handleManualViews(clip.id)}
                                          disabled={savingManualViews}
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          {t("admin.challenges.manualViewsSave")}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => { setManualViewsTarget(null); setManualViewsInput(""); }}
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          {t("common.cancel")}
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default AdminChallengeAudit;
